const BILL_DIVIDER = "--------------------------------";

const clean = (value, fallback = "To be discussed") => {
  const text = value == null ? "" : String(value).trim();
  return text || fallback;
};

export const formatBillPrice = (value) => {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "Price on request";
  return `INR ${Number(value).toLocaleString("en-IN")}`;
};

export const formatDeliveryTime = (days) => {
  const value = Number(days);
  if (!Number.isInteger(value) || value < 1) return "To be confirmed by the studio";
  return `${value} day${value === 1 ? "" : "s"}`;
};

export const formatBookingDateTime = (date = new Date()) => {
  const value = date instanceof Date ? date : new Date(date);
  return `${new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(value)} IST`;
};

const productCategory = (product) =>
  [product?.category, product?.subcategory].filter(Boolean).join(" / ") || "To be discussed";

const productDetails = ({ product, size, color, quantity = 1 }) => {
  const unitPrice = product?.offer_price ?? product?.price ?? product?.original_price;
  const total = unitPrice == null ? null : Number(unitPrice) * Number(quantity || 1);
  const details = [
    `*Product:* ${clean(product?.name)}`,
    `*Category:* ${productCategory(product)}`,
    `*Size:* ${clean(size)}`,
    `*Colour:* ${clean(color)}`,
    `*Quantity:* ${quantity || 1}`,
    `*Unit price:* ${formatBillPrice(unitPrice)}`,
    `*Estimated delivery:* ${formatDeliveryTime(product?.delivery_time_days)}`,
  ];
  if (product?.customization) details.push(`*Customisation:* ${clean(product.customization)}`);
  if (total != null) details.push(`*Line total:* ${formatBillPrice(total)}`);
  return details;
};

const closingLines = () => [
  "*House assurance:* Quality tested | 100% genuine | Craft assured",
  "",
  "_This is a booking summary. Final confirmation and payment instructions will be shared by the studio._",
  "",
  "I would like to discuss more about this order. Please help me with customisation, delivery and the next steps.",
];

export const buildProductBookingBill = ({ brandName = "LALITA SOUJANYA", product, size, color, quantity = 1, now }) => {
  const unitPrice = product?.offer_price ?? product?.original_price;
  const total = unitPrice == null ? null : Number(unitPrice) * Number(quantity || 1);
  return [
    `*${clean(product?.name, "PRODUCT").toUpperCase()}*`,
    "*BOOKING BILL*",
    `_${clean(brandName, "LALITA SOUJANYA")} Atelier_`,
    BILL_DIVIDER,
    `*Date & time:* ${formatBookingDateTime(now)}`,
    "",
    ...productDetails({ product, size, color, quantity }),
    ...(total != null ? ["", `*BOOKING TOTAL:* ${formatBillPrice(total)}`] : []),
    BILL_DIVIDER,
    ...closingLines(),
  ].join("\n");
};

export const buildOrderBookingBill = ({
  brandName = "LALITA SOUJANYA",
  orderId,
  items = [],
  customer = {},
  total,
  notes,
  now,
}) => {
  const firstName = clean(items[0]?.name, "ORDER");
  const heading = items.length > 1 ? `${firstName} + ${items.length - 1} MORE` : firstName;
  const itemLines = items.flatMap((item, index) => [
    `*ITEM ${index + 1} — ${clean(item.name).toUpperCase()}*`,
    ...productDetails({
      product: item,
      size: item.size,
      color: item.color,
      quantity: item.quantity,
    }).map((line) => `  ${line}`),
    "",
  ]);

  return [
    `*${heading.toUpperCase()}*`,
    "*ORDER BOOKING BILL*",
    `_${clean(brandName, "LALITA SOUJANYA")} Atelier_`,
    BILL_DIVIDER,
    `*Order ID:* ${clean(orderId, "Pending")}`,
    `*Date & time:* ${formatBookingDateTime(now)}`,
    "",
    "*CUSTOMER DETAILS*",
    `*Name:* ${clean(customer.name)}`,
    `*Phone:* ${clean(customer.phone)}`,
    ...(customer.email ? [`*Email:* ${clean(customer.email)}`] : []),
    `*Delivery address:* ${clean(customer.address)}`,
    ...(notes ? [`*Customer note:* ${clean(notes)}`] : []),
    "",
    "*BOOKED PRODUCTS*",
    ...itemLines,
    BILL_DIVIDER,
    `*BOOKING TOTAL:* ${formatBillPrice(total)}`,
    ...closingLines(),
  ].join("\n");
};

